import React, { useEffect, useState, useMemo } from 'react';
import { formatGermanTime } from '../../helpers/germanTime';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EmailIcon from '@mui/icons-material/Email';
import PeopleIcon from '@mui/icons-material/People';
import VisibilityIcon from '@mui/icons-material/Visibility';
import MouseIcon from '@mui/icons-material/Mouse';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import CampaignIcon from '@mui/icons-material/Campaign';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import EuroIcon from '@mui/icons-material/Euro';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { fetchData } from '../../helpers/apiUtils';
import SubjectAbBlock from './SubjectAbBlock';
import { formatNumber } from '../../helpers/numberUtils';
import { MailingType } from '../../helpers/webinarMailingData';
import { fetchCpaPercentage } from '../../helpers/plxOrdersData';

interface CampaignSummary {
  id: number;
  name: string;
  mailingType: MailingType;
  status: string;
  webinarTitle: string | null;
  webinarDate: string | null;
  emailsSent: string; // e.g. "2/3"
  totalRecipients: number;
  totalOpens: number;
  avgOpenRate: number;
  totalClicks: number;
  avgClickRate: number;
  avgCtr: number;
  totalUnsubscribes: number;
  totalRegistrations: number;
  totalAnfos: number;
  totalRevenue: number;
  totalCpaRevenue: number;
  cpaPerRecipient: number;
  anfoRate: number;
  registrationRate: number;
  createdAt: string;
  followupAssistedAnfos?: number;
  followupAssistedShare?: number;
  followupAssistedRevenue?: number;
}

interface EvaluationResponse {
  campaigns: CampaignSummary[];
  summary: {
    totalCampaigns: number;
    totalRecipients: number;
    totalOpens: number;
    avgOpenRate: number;
    totalClicks: number;
    avgCtr: number;
    totalRegistrations: number;
    totalAnfos: number;
    totalRevenue: number;
    totalCpaRevenue: number;
    avgAnfoRate: number;
    avgConversionRate: number;
    followupAssistedAnfos?: number;
    followupAssistedShare?: number;
    followupAssistedRevenue?: number;
  };
}

interface ScheduleMetrics {
  recipients: number;
  opens: number;
  openRate: number;
  clicks: number;
  clickRate: number;
  ctr: number;
  unsubscribes: number;
  registrations: number;
  anfos: number;
  revenue: number;
  cpaRevenue: number;
  cpaPerRecipient: number;
  anfoRate: number;
  registrationRate: number;
}

interface ScheduleDetail {
  scheduleId: number | string;
  mailingNumber: number;
  subject: string;
  clientCode: string;
  espType: string;
  scheduledDatetime: string;
  sentAt: string | null;
  status: string;
  isNonOpener?: boolean;
  metrics: ScheduleMetrics | null;
}

interface CampaignDetailResponse {
  campaign: CampaignSummary;
  schedules: ScheduleDetail[];
}

interface ClientSummary {
  clientCode: string;
  clientName: string;
  espType: string;
  totalMailings: number;
  totalRecipients: number;
  avgRecipientsPerMailing: number;
  avgOpenRate: number;
  avgCtr: number;
  totalRegistrations: number;
  avgRegistrationRate: number;
  totalAnfos: number;
  avgAnfoRate: number;
  avgCpaPerMailing: number;
  avgCpaPerRecipient: number;
  totalCpaRevenue: number;
  totalRevenue: number;
  avgRevenuePerMailing: number;
  followupAssistedAnfos?: number;
  followupAssistedShare?: number;
  followupAssistedRevenue?: number;
}

interface MailingSummary {
  mailingNumber: number;
  totalMailings: number;
  totalRecipients: number;
  avgRecipientsPerMailing: number;
  avgOpenRate: number;
  avgCtr: number;
  totalRegistrations: number;
  avgRegistrationRate: number;
  totalAnfos: number;
  avgAnfoRate: number;
  avgCpaPerMailing: number;
  cpaEtkp: number;
  totalCpaRevenue: number;
}

// Documentation Legend Component
const DocumentationLegend: React.FC<{
  expanded: boolean;
  onToggle: () => void;
  cpaPercentage: number;
}> = ({ expanded, onToggle, cpaPercentage }) => (
  <Box sx={{ mb: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
    <Box
      sx={{
        p: 2,
        backgroundColor: '#f5f5f5',
        cursor: 'pointer',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}
      onClick={onToggle}
    >
      <Typography variant="h6" fontWeight="bold">
        📊 Kennzahlen-Dokumentation
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {expanded ? '▼ Ausblenden' : '▶ Anzeigen'}
      </Typography>
    </Box>

    {expanded && (
      <Box sx={{ p: 3 }}>
        {/* Section 1: Basis-Metriken */}
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          1. Basis-Metriken (Email-Performance)
        </Typography>
        <Box sx={{ pl: 2, mb: 2 }}>
          <Typography variant="body2" paragraph>
            <strong>Empfänger:</strong> Anzahl der Emails, die an die Empfängerlisten gesendet
            wurden (aus ESP-Metriken: Mailchimp + Iterable)
          </Typography>
          <Typography variant="body2" paragraph>
            <strong>Öffnungen:</strong> Anzahl der einzigartigen Email-Öffnungen
          </Typography>
          <Typography variant="body2" paragraph>
            <strong>Öffnungsrate:</strong> (Öffnungen / Empfänger) × 100 - Prozentsatz der
            Empfänger, die die Email geöffnet haben
          </Typography>
          <Typography variant="body2" paragraph>
            <strong>Klicks:</strong> Anzahl der einzigartigen Klicks auf Links in der Email
          </Typography>
          <Typography variant="body2" paragraph>
            <strong>CTR (Click-Through Rate):</strong> (Klicks / Öffnungen) × 100 - Prozentsatz der
            Öffner, die geklickt haben
          </Typography>
        </Box>

        {/* Section 2: Webinar-Registrierungen */}
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          2. Webinar-Registrierungen
        </Typography>
        <Box sx={{ pl: 2, mb: 2 }}>
          <Typography variant="body2" paragraph>
            <strong>Registrierungen:</strong> Anzahl der Nutzer, die sich über die Email-Kampagne
            für das Webinar registriert haben
          </Typography>
          <Typography variant="body2" sx={{ pl: 2 }} paragraph>
            → Datenquelle: <code>adserver_webinar_registers</code> mit ESP-Campaign-IDs
            (it_campaign_id / mc_campaign_id)
          </Typography>
          <Typography variant="body2" paragraph>
            <strong>Reg-Rate (Registrierungsrate):</strong> (Registrierungen / Empfänger) × 100 -
            Prozentsatz der Empfänger, die sich registriert haben
          </Typography>
        </Box>

        {/* Section 3: ANFO (Bestellungen) */}
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          3. ANFOs (Anforderungen)
        </Typography>
        <Box sx={{ pl: 2, mb: 2 }}>
          <Typography variant="body2" paragraph>
            <strong>ANFO:</strong> &quot;Anforderung&quot; - Anzahl der Bestellungen von Nutzern,
            die sich für das Webinar registriert haben
          </Typography>
          <Typography variant="body2" sx={{ pl: 2 }} paragraph>
            → Identifikation: Zuordnung über <code>webinar_order_mapping</code> (Priorität: RID &gt;
            Banner-ID &gt; Email+Datum &gt; Email+DOI &gt; Email+Affiliate)
          </Typography>
          <Typography variant="body2" sx={{ pl: 2 }} paragraph>
            → Webinar-Matching: Vorberechnete Zuordnungstabelle (nur Bestellungen VON DIESEM
            Webinar)
          </Typography>
          <Typography variant="body2" sx={{ pl: 2 }} paragraph>
            → Filter: Abgelehnte Bestellungen (vertrag_sts = &apos;V&apos;) werden ausgeschlossen
          </Typography>
          <Typography variant="body2" sx={{ pl: 2 }} paragraph>
            → Pro Kunde: Nur die erste Bestellung pro Webinar wird gezählt
          </Typography>
          <Typography variant="body2" sx={{ pl: 2 }} paragraph>
            → Zeitfilter: Bestellung muss nach Kampagnen-Erstellung erfolgt sein
          </Typography>
          <Typography variant="body2" paragraph>
            <strong>Anfos je Registrierung:</strong> (Anfos ÷ Registrierungen) × 100 — Prozentsatz
            der Registrierten, die bestellt haben
          </Typography>
        </Box>

        {/* Section 4: Umsatz */}
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          4. Umsatz - Tatsächlicher Zahlungseingang
        </Typography>
        <Box sx={{ pl: 2, mb: 2 }}>
          <Typography variant="body2" paragraph>
            <strong>Umsatz:</strong> Tatsächlich eingegangene Zahlungen (echtes Geld auf dem Konto)
          </Typography>
          <Typography variant="body2" sx={{ pl: 2 }} paragraph>
            → Quelle: <code>plx_order_metrics.actual_revenue</code> (= <code>zahlung_brutto</code>{' '}
            aus PLX)
          </Typography>
          <Typography variant="body2" sx={{ pl: 2 }} paragraph>
            → Berücksichtigt automatisch: Stornierungen, Teilzahlungen, Chargebacks,
            Zahlungsausfälle
          </Typography>
          <Typography
            variant="body2"
            sx={{ pl: 2, fontStyle: 'italic', color: '#ed6c02' }}
            paragraph
          >
            ⚠️ <strong>Neue Kampagnen (0-30 Tage):</strong> Zeigen meist €0 Umsatz, weil Kunden noch
            in der Trial-Phase sind und keine Zahlung erfolgt ist. Das ist normal!
          </Typography>
          <Typography
            variant="body2"
            sx={{ pl: 2, fontStyle: 'italic', color: '#2e7d32' }}
            paragraph
          >
            ✓ <strong>Für Kampagnen-Bewertung:</strong> Nutze CPA € statt Umsatz! CPA € zeigt das
            Akquisitionsbudget, das die Kampagne rechtfertigt.
          </Typography>
          <Typography
            variant="body2"
            sx={{ pl: 2, fontStyle: 'italic', color: '#1976d2' }}
            paragraph
          >
            ℹ️ <strong>Umsatz dient zum späteren Vergleich:</strong> Wie viel echtes Geld wurde mit
            dieser Kampagne bereits verdient?
          </Typography>
        </Box>

        {/* Section 5: CPA */}
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          5. CPA (Cost Per Acquisition) - Marketing-Budget-Anteil
        </Typography>
        <Box sx={{ pl: 2, mb: 2 }}>
          <Typography variant="body2" paragraph>
            <strong>CPA ist keine Ist-Kosten-Zahl</strong> — sondern der Teil des Anfo-Werts, der
            dem Marketing-Kanal zugerechnet werden darf.
          </Typography>
          <Typography variant="body2" sx={{ pl: 2 }} paragraph>
            → Kostenstruktur intern: <strong>{100 - cpaPercentage} % Anfo-Wert</strong> decken
            Produktion (Texter, PM, Börsenbrief-Autoren, Infrastruktur),{' '}
            <strong>{cpaPercentage} % sind Marketing-Budget</strong> (E-Mail, Ads,
            Webinar-Produktion, Affiliates).
          </Typography>
          <Typography variant="body2" sx={{ pl: 2 }} paragraph>
            → Daher: <code>anfo_cpa = anfo_wert × {cpaPercentage} %</code> — das Maximal-Budget pro
            Lead, damit sich Akquise wirtschaftlich rechnet. Aktueller Split konfigurierbar unter{' '}
            <code>/plx-orders → Produkte → CPA-Prozentsatz-Slider</code>.
          </Typography>
          <Typography variant="body2" paragraph>
            <strong>CPA-Umsatz:</strong> Summe der CPA-Beträge aller ANFO- Bestellungen dieser
            Kampagne. Zeigt wie viel dieses Mailing zur Deckung seines eigenen Marketing-Budgets
            beigetragen hat.
          </Typography>
          <Typography variant="body2" paragraph>
            <strong>CPA € / Empfänger:</strong> CPA-Umsatz / Empfänger — der pro-Mail-Anteil am
            Marketing-Budget. Liegt dieser Wert über den tatsächlichen Versand-/Produktions-Kosten
            pro Mail → <strong>profitabel</strong>, darunter → drauf gezahlt.
          </Typography>
          <Typography variant="body2" sx={{ pl: 2 }} paragraph>
            → Anfos ohne CPA-Wert zählen 0 (kein Rückfall auf den Anfo-Wert)
          </Typography>
        </Box>

        {/* Section 6: KPI-Leitfaden nach Kampagnenalter */}
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          6. KPI-Leitfaden nach Kampagnenalter
        </Typography>
        <Box sx={{ pl: 2, mb: 2 }}>
          <Typography variant="body2" fontWeight="bold" paragraph>
            Phase 1: Erste 30 Tage (ENGAGEMENT-FOKUS)
          </Typography>
          <Typography variant="body2" sx={{ pl: 2, color: '#2e7d32' }} paragraph>
            ✅ Diese KPIs verwenden: Registrierungsrate, Anfos je Registrierung, CPA-Wert /
            Empfänger
          </Typography>
          <Typography variant="body2" sx={{ pl: 2, color: '#ed6c02' }} paragraph>
            ⚠️ Umsatz = €0 ist normal! Kunden sind noch in der Trial-Phase (keine Zahlungen)
          </Typography>
          <Typography variant="body2" sx={{ pl: 2, fontStyle: 'italic' }} paragraph>
            → Kampagnen-Bewertung: Ausschließlich über CPA € (zeigt Akquisitionsbudget)
          </Typography>

          <Typography variant="body2" fontWeight="bold" paragraph sx={{ mt: 2 }}>
            Phase 2: 30-90 Tage (ERSTE ZAHLUNGEN)
          </Typography>
          <Typography variant="body2" sx={{ pl: 2, color: '#2e7d32' }} paragraph>
            ✅ Diese KPIs verwenden: CPA € / Empfänger, Umsatz (erste echte Zahlungen sichtbar)
          </Typography>
          <Typography variant="body2" sx={{ pl: 2, fontStyle: 'italic' }} paragraph>
            → Umsatz steigt durch erste Quartals-Zahlungen nach Trial-Ende
          </Typography>

          <Typography variant="body2" fontWeight="bold" paragraph sx={{ mt: 2 }}>
            Phase 3: 90+ Tage (REALISTISCHE BEWERTUNG)
          </Typography>
          <Typography variant="body2" sx={{ pl: 2, color: '#2e7d32' }} paragraph>
            ✅ Diese KPIs verwenden: Umsatz (echte Einnahmen), Umsatz pro ANFO
          </Typography>
          <Typography variant="body2" sx={{ pl: 2, fontStyle: 'italic' }} paragraph>
            → Umsatz zeigt jetzt realistisches Bild der tatsächlichen Einnahmen
          </Typography>
          <Typography
            variant="body2"
            sx={{ pl: 2, fontWeight: 'bold', color: '#1976d2' }}
            paragraph
          >
            → ROI-Bewertung erfolgt auf Webinar/Produkt-Ebene, nicht pro Kampagne
          </Typography>
        </Box>

        {/* Section 7: Wichtige Hinweise */}
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          7. Wichtige Hinweise
        </Typography>
        <Box sx={{ pl: 2 }}>
          <Typography variant="body2" paragraph>
            • <strong>Attribution:</strong> Registrierungen und Bestellungen werden über
            ESP-Campaign-IDs zugeordnet (it_campaign_id / mc_campaign_id)
          </Typography>
          <Typography variant="body2" paragraph>
            • <strong>Zeitzone:</strong> Alle Datumsangaben in CET (Central European Time)
          </Typography>
          <Typography variant="body2" paragraph>
            • <strong>Cache:</strong> Umsatz-Metriken werden alle 6 Stunden aktualisiert
            (plx_order_metrics)
          </Typography>
          <Typography variant="body2" paragraph>
            • <strong>Summen in Details:</strong> Subtotalen pro Mailing-Nummer aggregieren alle
            Client-Schedules
          </Typography>
        </Box>
      </Box>
    )}
  </Box>
);

// Summary Card Component
const SummaryCard: React.FC<{
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color?: string;
}> = ({ title, value, icon, color = '#1976d2' }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box>
          <Typography color="text.secondary" variant="body2" gutterBottom>
            {title}
          </Typography>
          <Typography variant="h5" fontWeight="bold">
            {value}
          </Typography>
        </Box>
        <Box sx={{ color, opacity: 0.7, fontSize: 40 }}>{icon}</Box>
      </Box>
    </CardContent>
  </Card>
);

const EvaluationTab: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<EvaluationResponse | null>(null);
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [selectedCampaign, setSelectedCampaign] = useState<CampaignSummary | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailData, setDetailData] = useState<CampaignDetailResponse | null>(null);
  const [legendExpanded, setLegendExpanded] = useState(false);
  const [clientData, setClientData] = useState<ClientSummary[] | null>(null);
  const [clientFilter, setClientFilter] = useState<string | null>(null);
  const [mailingData, setMailingData] = useState<MailingSummary[] | null>(null);
  const [mailingFilter, setMailingFilter] = useState<number | null>(null);
  const [normalizeInactive, setNormalizeInactive] = useState(false);
  const [cpaPercentage, setCpaPercentage] = useState<number>(40);
  useEffect(() => {
    fetchCpaPercentage()
      .then(setCpaPercentage)
      .catch(() => {
        /* fallback to 40 */
      });
  }, []);
  const [includeFollowups, setIncludeFollowups] = useState(true);

  // Load evaluation data
  useEffect(() => {
    loadData();
    loadClientData();
    loadMailingData();
  }, [typeFilter, clientFilter, mailingFilter, normalizeInactive, includeFollowups]);

  const loadData = async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      if (typeFilter !== 'all') {
        params.append('mailing_type', typeFilter);
      }
      if (clientFilter) {
        params.append('client_code', clientFilter);
      }
      if (mailingFilter) {
        params.append('mailing_number', mailingFilter.toString());
      }
      if (normalizeInactive) {
        params.append('normalize', '1');
      }
      // Followup semantics only apply on non-drill-down views — mailing_number
      // filters to main's sequence position (1-5) which followups don't share.
      if (includeFollowups && mailingFilter === null) {
        params.append('include_followups', '1');
      }

      const response = await fetchData<EvaluationResponse>(
        `/api/webinar-mailings/evaluation/campaigns?${params.toString()}`,
      );

      if (response.success && response.data) {
        setData(response.data);
      } else {
        setError(response.error || 'Fehler beim Laden der Auswertung');
      }
    } catch (err) {
      setError('Fehler beim Laden der Auswertung');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadClientData = async () => {
    try {
      const params = new URLSearchParams();
      if (typeFilter !== 'all') {
        params.append('mailing_type', typeFilter);
      }
      if (normalizeInactive) {
        params.append('normalize', '1');
      }
      if (includeFollowups) {
        params.append('include_followups', '1');
      }

      const response = await fetchData<{ clients: ClientSummary[] }>(
        `/api/webinar-mailings/evaluation/client-summary?${params.toString()}`,
      );

      if (response.success && response.data) {
        setClientData(response.data.clients);
      }
    } catch (err) {
      console.error('Error loading client data:', err);
    }
  };

  const loadMailingData = async () => {
    try {
      const params = new URLSearchParams();
      if (typeFilter !== 'all') {
        params.append('mailing_type', typeFilter);
      }
      if (clientFilter) {
        params.append('client_code', clientFilter);
      }
      if (normalizeInactive) {
        params.append('normalize', '1');
      }

      const response = await fetchData<{ mailings: MailingSummary[] }>(
        `/api/webinar-mailings/evaluation/mailing-summary?${params.toString()}`,
      );

      if (response.success && response.data) {
        setMailingData(response.data.mailings);
      }
    } catch (err) {
      console.error('Error loading mailing data:', err);
    }
  };

  const handleTypeChange = (event: SelectChangeEvent<string>) => {
    setTypeFilter(event.target.value);
  };

  const handleCampaignClick = async (campaign: CampaignSummary) => {
    setSelectedCampaign(campaign);
    setDetailLoading(true);
    setDetailData(null);

    try {
      const params = new URLSearchParams();
      if (clientFilter) {
        params.append('client_code', clientFilter);
      }
      const response = await fetchData<CampaignDetailResponse>(
        `/api/webinar-mailings/evaluation/campaigns/${campaign.id}/details?${params.toString()}`,
      );

      if (response.success && response.data) {
        setDetailData(response.data);
      }
    } catch (err) {
      console.error('Error loading campaign details:', err);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleCloseDetail = () => {
    setSelectedCampaign(null);
    setDetailData(null);
  };

  // Format helpers
  const formatPercent = (value: number) => `${(value * 100).toFixed(1)}%`;
  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value || 0);
  };
  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('de-DE');
  };

  // Group schedules by mailing number for detail view
  const groupedSchedules = useMemo(() => {
    if (!detailData?.schedules) return [];

    const grouped: Record<
      number,
      { mailingNumber: number; subject: string; schedules: ScheduleDetail[] }
    > = {};

    detailData.schedules.forEach((s) => {
      if (!grouped[s.mailingNumber]) {
        grouped[s.mailingNumber] = {
          mailingNumber: s.mailingNumber,
          subject: s.subject,
          schedules: [],
        };
      }
      grouped[s.mailingNumber].schedules.push(s);
    });

    return Object.values(grouped).sort((a, b) => a.mailingNumber - b.mailingNumber);
  }, [detailData]);

  if (loading && !data) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Documentation Legend */}
      <DocumentationLegend
        expanded={legendExpanded}
        onToggle={() => setLegendExpanded(!legendExpanded)}
        cpaPercentage={cpaPercentage}
      />

      {/* Filters */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Mailing-Typ</InputLabel>
          <Select value={typeFilter} onChange={handleTypeChange} label="Mailing-Typ">
            <MenuItem value="all">Alle</MenuItem>
            <MenuItem value="heatup">Heat-Up</MenuItem>
            <MenuItem value="redlink">Redlink</MenuItem>
          </Select>
        </FormControl>

        <Box display="flex" alignItems="center">
          <FormControlLabel
            control={
              <Switch
                checked={normalizeInactive}
                onChange={(e) => setNormalizeInactive(e.target.checked)}
                size="small"
              />
            }
            label="Inaktive normalisieren"
          />
          <Tooltip title="Samstags-Sends mit Inaktiven werden auf den Durchschnitt der letzten 10 normalen Sends normalisiert, um faire Vergleiche zu ermöglichen.">
            <InfoOutlinedIcon
              sx={{
                fontSize: 16,
                ml: 0.5,
                color: 'text.secondary',
                cursor: 'help',
              }}
            />
          </Tooltip>
        </Box>

        <Box display="flex" alignItems="center">
          <FormControlLabel
            control={
              <Switch
                checked={includeFollowups && mailingFilter === null}
                onChange={(e) => setIncludeFollowups(e.target.checked)}
                size="small"
                disabled={mailingFilter !== null}
              />
            }
            label="inkl. Followups"
          />
          <Tooltip
            title={
              mailingFilter !== null
                ? 'Followup-Beiträge lassen sich keiner Mailing-Nummer zuordnen. Filter entfernen, um die Followup-Sicht zu aktivieren.'
                : 'AN: komplette Sicht – Followup-Empfänger werden zu Empfängern/Öffnungen/Klicks addiert, alle ANFOs gezählt (inkl. Followup-assistierte). AUS: nur Haupt-Mailing ohne Followup-Lift – die Followup-assistierten ANFOs werden abgezogen, damit die Differenz sichtbar wird.'
            }
          >
            <InfoOutlinedIcon
              sx={{
                fontSize: 16,
                ml: 0.5,
                color: 'text.secondary',
                cursor: 'help',
              }}
            />
          </Tooltip>
        </Box>
      </Box>

      {/* Summary Cards */}
      {data?.summary && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={2}>
            <SummaryCard
              title="Kampagnen"
              value={data.summary.totalCampaigns}
              icon={<CampaignIcon fontSize="inherit" />}
              color="#1976d2"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <SummaryCard
              title="Empfänger"
              value={formatNumber(data.summary.totalRecipients, 0)}
              icon={<PeopleIcon fontSize="inherit" />}
              color="#2e7d32"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <SummaryCard
              title="Öffnungen"
              value={formatNumber(data.summary.totalOpens, 0)}
              icon={<VisibilityIcon fontSize="inherit" />}
              color="#ed6c02"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <SummaryCard
              title="Ø Öffnungsrate"
              value={formatPercent(data.summary.avgOpenRate)}
              icon={<EmailIcon fontSize="inherit" />}
              color="#9c27b0"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <SummaryCard
              title="Ø CTR"
              value={formatPercent(data.summary.avgCtr)}
              icon={<MouseIcon fontSize="inherit" />}
              color="#0288d1"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <SummaryCard
              title="Registrierungen"
              value={formatNumber(data.summary.totalRegistrations, 0)}
              icon={<HowToRegIcon fontSize="inherit" />}
              color="#d32f2f"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <SummaryCard
              title="ANFOs"
              value={formatNumber(data.summary.totalAnfos, 0)}
              icon={<ShoppingCartIcon fontSize="inherit" />}
              color="#388e3c"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <SummaryCard
              title="Umsatz (real)"
              value={formatCurrency(data.summary.totalRevenue)}
              icon={<EuroIcon fontSize="inherit" />}
              color="#f57c00"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <SummaryCard
              title="Ø Anfos je Registrierung"
              value={formatPercent(data.summary.avgAnfoRate)}
              icon={<TrendingUpIcon fontSize="inherit" />}
              color="#7b1fa2"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <SummaryCard
              title="Ø Reg-Rate"
              value={formatPercent(data.summary.avgConversionRate)}
              icon={<PersonAddIcon fontSize="inherit" />}
              color="#0277bd"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <SummaryCard
              title="CPA-Umsatz"
              value={formatCurrency(data.summary.totalCpaRevenue)}
              icon={<EuroIcon fontSize="inherit" />}
              color="#1976d2"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <SummaryCard
              title="CPA ETKP"
              value={formatCurrency(
                data.summary.totalRecipients > 0
                  ? (data.summary.totalCpaRevenue / data.summary.totalRecipients) * 1000
                  : 0,
              )}
              icon={<TrendingUpIcon fontSize="inherit" />}
              color="#00897b"
            />
          </Grid>
          {data.summary.followupAssistedAnfos !== undefined && (
            <Grid item xs={12} sm={6} md={2}>
              <SummaryCard
                title="Followup-assistierte ANFOs"
                value={`${formatNumber(
                  data.summary.followupAssistedAnfos,
                  0,
                )} (${formatPercent(data.summary.followupAssistedShare || 0)})`}
                icon={<TrendingUpIcon fontSize="inherit" />}
                color="#6a1b9a"
              />
            </Grid>
          )}
        </Grid>
      )}

      {/* Filter Indicators */}
      {(clientFilter || mailingFilter) && (
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2">Gefiltert nach:</Typography>
          {clientFilter && (
            <Chip
              label={
                clientData?.find((c) => c.clientCode === clientFilter)?.clientName || clientFilter
              }
              onDelete={() => setClientFilter(null)}
              color="primary"
              size="small"
            />
          )}
          {mailingFilter && (
            <Chip
              label={`Mailing #${mailingFilter}`}
              onDelete={() => setMailingFilter(null)}
              color="secondary"
              size="small"
            />
          )}
        </Box>
      )}

      {/* Campaign Table */}
      <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  backgroundColor: '#e0e0e0 !important',
                  fontWeight: 'bold',
                }}
              >
                Kampagne
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: '#e0e0e0 !important',
                  fontWeight: 'bold',
                }}
              >
                Typ
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: '#e0e0e0 !important',
                  fontWeight: 'bold',
                }}
              >
                Webinar
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: '#e0e0e0 !important',
                  fontWeight: 'bold',
                }}
                align="center"
              >
                Mails
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: '#e0e0e0 !important',
                  fontWeight: 'bold',
                }}
                align="right"
              >
                Empfänger
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: '#e0e0e0 !important',
                  fontWeight: 'bold',
                }}
                align="right"
              >
                Öffnungen
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: '#e0e0e0 !important',
                  fontWeight: 'bold',
                }}
                align="right"
              >
                Öffnungs-%
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: '#e0e0e0 !important',
                  fontWeight: 'bold',
                }}
                align="right"
              >
                Klicks
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: '#e0e0e0 !important',
                  fontWeight: 'bold',
                }}
                align="right"
              >
                CTR
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: '#e0e0e0 !important',
                  fontWeight: 'bold',
                }}
                align="right"
              >
                Registr.
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: '#e0e0e0 !important',
                  fontWeight: 'bold',
                }}
                align="right"
              >
                ANFOs
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: '#e0e0e0 !important',
                  fontWeight: 'bold',
                }}
                align="right"
              >
                Umsatz (real)
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: '#e0e0e0 !important',
                  fontWeight: 'bold',
                }}
                align="right"
              >
                CPA €
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: '#e0e0e0 !important',
                  fontWeight: 'bold',
                }}
                align="right"
              >
                CPA € / Empf.
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: '#e0e0e0 !important',
                  fontWeight: 'bold',
                }}
                align="right"
              >
                CPA ETKP
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: '#e0e0e0 !important',
                  fontWeight: 'bold',
                }}
                align="right"
              >
                ANFO %
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: '#e0e0e0 !important',
                  fontWeight: 'bold',
                }}
                align="right"
              >
                Reg %
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.campaigns.map((campaign) => (
              <TableRow
                key={campaign.id}
                hover
                sx={{
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: '#f5f5f5' },
                }}
                onClick={() => handleCampaignClick(campaign)}
              >
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">
                    {campaign.name}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography
                    variant="caption"
                    sx={{
                      px: 1,
                      py: 0.25,
                      borderRadius: 1,
                      backgroundColor: campaign.mailingType === 'heatup' ? '#e3f2fd' : '#fff3e0',
                      color: campaign.mailingType === 'heatup' ? '#1565c0' : '#e65100',
                    }}
                  >
                    {campaign.mailingType === 'heatup' ? 'Heat-Up' : 'Redlink'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                    {campaign.webinarTitle || '-'}
                  </Typography>
                  {campaign.webinarDate && (
                    <Typography variant="caption" color="text.secondary">
                      {formatDate(campaign.webinarDate)}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="center">
                  <Typography variant="body2">{campaign.emailsSent}</Typography>
                </TableCell>
                <TableCell align="right">{formatNumber(campaign.totalRecipients, 0)}</TableCell>
                <TableCell align="right">{formatNumber(campaign.totalOpens, 0)}</TableCell>
                <TableCell align="right">{formatPercent(campaign.avgOpenRate)}</TableCell>
                <TableCell align="right">{formatNumber(campaign.totalClicks, 0)}</TableCell>
                <TableCell align="right">{formatPercent(campaign.avgCtr)}</TableCell>
                <TableCell align="right">
                  <Typography fontWeight="medium" color="primary">
                    {formatNumber(campaign.totalRegistrations, 0)}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography fontWeight="medium" color="success.main">
                    {formatNumber(campaign.totalAnfos, 0)}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2">{formatCurrency(campaign.totalRevenue)}</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" color="primary.main">
                    {formatCurrency(campaign.totalCpaRevenue)}
                  </Typography>
                </TableCell>
                <TableCell align="right">{formatCurrency(campaign.cpaPerRecipient)}</TableCell>
                <TableCell align="right">
                  {formatCurrency(
                    campaign.totalRecipients > 0
                      ? (campaign.totalCpaRevenue / campaign.totalRecipients) * 1000
                      : 0,
                  )}
                </TableCell>
                <TableCell align="right">{formatPercent(campaign.anfoRate)}</TableCell>
                <TableCell align="right">{formatPercent(campaign.registrationRate)}</TableCell>
              </TableRow>
            ))}
            {data?.campaigns.length === 0 && (
              <TableRow>
                <TableCell colSpan={17} align="center">
                  <Typography color="text.secondary" py={2}>
                    Keine Kampagnen mit versendeten Mailings gefunden
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Client Summary Table */}
      {clientData && clientData.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Übersicht nach Client
          </Typography>
          <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      backgroundColor: '#e0e0e0 !important',
                      fontWeight: 'bold',
                    }}
                  >
                    Client
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: '#e0e0e0 !important',
                      fontWeight: 'bold',
                    }}
                  >
                    ESP
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: '#e0e0e0 !important',
                      fontWeight: 'bold',
                    }}
                    align="right"
                  >
                    Mailings
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: '#e0e0e0 !important',
                      fontWeight: 'bold',
                    }}
                    align="right"
                  >
                    Ø Empfänger
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: '#e0e0e0 !important',
                      fontWeight: 'bold',
                    }}
                    align="right"
                  >
                    Ø Öffnungsrate
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: '#e0e0e0 !important',
                      fontWeight: 'bold',
                    }}
                    align="right"
                  >
                    Ø CTR
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: '#e0e0e0 !important',
                      fontWeight: 'bold',
                    }}
                    align="right"
                  >
                    Ø Reg-Rate
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: '#e0e0e0 !important',
                      fontWeight: 'bold',
                    }}
                    align="right"
                  >
                    Ø Anfos / Reg.
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: '#e0e0e0 !important',
                      fontWeight: 'bold',
                    }}
                    align="right"
                  >
                    Ø CPA € / Mailing
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: '#e0e0e0 !important',
                      fontWeight: 'bold',
                    }}
                    align="right"
                  >
                    CPA ETKP
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: '#e0e0e0 !important',
                      fontWeight: 'bold',
                    }}
                    align="right"
                  >
                    CPA € Gesamt
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: '#e0e0e0 !important',
                      fontWeight: 'bold',
                    }}
                    align="right"
                  >
                    Umsatz Gesamt
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {clientData.map((client) => (
                  <TableRow
                    key={client.clientCode}
                    hover
                    sx={{
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: '#f5f5f5' },
                      backgroundColor:
                        clientFilter === client.clientCode ? '#e3f2fd' : 'transparent',
                    }}
                    onClick={() =>
                      setClientFilter(clientFilter === client.clientCode ? null : client.clientCode)
                    }
                  >
                    <TableCell>
                      <Typography fontWeight="medium">{client.clientName}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="caption"
                        sx={{
                          px: 1,
                          py: 0.25,
                          borderRadius: 1,
                          backgroundColor: client.espType === 'iterable' ? '#e8f5e9' : '#fff3e0',
                          color: client.espType === 'iterable' ? '#2e7d32' : '#e65100',
                        }}
                      >
                        {client.espType === 'iterable' ? 'Iterable' : 'Mailchimp'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{client.totalMailings}</TableCell>
                    <TableCell align="right">
                      {formatNumber(client.avgRecipientsPerMailing, 0)}
                    </TableCell>
                    <TableCell align="right">{formatPercent(client.avgOpenRate)}</TableCell>
                    <TableCell align="right">{formatPercent(client.avgCtr)}</TableCell>
                    <TableCell align="right">{formatPercent(client.avgRegistrationRate)}</TableCell>
                    <TableCell align="right">{formatPercent(client.avgAnfoRate)}</TableCell>
                    <TableCell align="right">{formatCurrency(client.avgCpaPerMailing)}</TableCell>
                    <TableCell align="right">
                      {formatCurrency(client.avgCpaPerRecipient * 1000)}
                    </TableCell>
                    <TableCell align="right">
                      <Typography fontWeight="medium" color="primary.main">
                        {formatCurrency(client.totalCpaRevenue)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography fontWeight="medium">
                        {formatCurrency(client.totalRevenue)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Klick auf Client-Zeile filtert die Kampagnen-Tabelle oben
          </Typography>
        </Box>
      )}

      {/* Betreff-A/B je Paar — erscheint erst, wenn es Paare gibt */}
      <SubjectAbBlock />

      {/* Mailing Number Summary Table */}
      {mailingData && mailingData.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Übersicht nach Mailing-Nummer
          </Typography>
          <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      backgroundColor: '#e0e0e0 !important',
                      fontWeight: 'bold',
                    }}
                  >
                    Mailing #
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: '#e0e0e0 !important',
                      fontWeight: 'bold',
                    }}
                    align="right"
                  >
                    Anzahl
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: '#e0e0e0 !important',
                      fontWeight: 'bold',
                    }}
                    align="right"
                  >
                    Ø Empfänger
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: '#e0e0e0 !important',
                      fontWeight: 'bold',
                    }}
                    align="right"
                  >
                    Ø Öffnungsrate
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: '#e0e0e0 !important',
                      fontWeight: 'bold',
                    }}
                    align="right"
                  >
                    Ø CTR
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: '#e0e0e0 !important',
                      fontWeight: 'bold',
                    }}
                    align="right"
                  >
                    Ø Reg-Rate
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: '#e0e0e0 !important',
                      fontWeight: 'bold',
                    }}
                    align="right"
                  >
                    Ø Anfos / Reg.
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: '#e0e0e0 !important',
                      fontWeight: 'bold',
                    }}
                    align="right"
                  >
                    Ø CPA € / Mailing
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: '#e0e0e0 !important',
                      fontWeight: 'bold',
                    }}
                    align="right"
                  >
                    CPA ETKP
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: '#e0e0e0 !important',
                      fontWeight: 'bold',
                    }}
                    align="right"
                  >
                    CPA € Gesamt
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {mailingData.map((mailing) => (
                  <TableRow
                    key={mailing.mailingNumber}
                    hover
                    sx={{
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: '#f5f5f5' },
                      backgroundColor:
                        mailingFilter === mailing.mailingNumber ? '#fce4ec' : 'transparent',
                    }}
                    onClick={() =>
                      setMailingFilter(
                        mailingFilter === mailing.mailingNumber ? null : mailing.mailingNumber,
                      )
                    }
                  >
                    <TableCell>
                      <Typography fontWeight="medium">Mailing #{mailing.mailingNumber}</Typography>
                    </TableCell>
                    <TableCell align="right">{mailing.totalMailings}</TableCell>
                    <TableCell align="right">
                      {formatNumber(mailing.avgRecipientsPerMailing, 0)}
                    </TableCell>
                    <TableCell align="right">{formatPercent(mailing.avgOpenRate)}</TableCell>
                    <TableCell align="right">{formatPercent(mailing.avgCtr)}</TableCell>
                    <TableCell align="right">
                      {formatPercent(mailing.avgRegistrationRate)}
                    </TableCell>
                    <TableCell align="right">{formatPercent(mailing.avgAnfoRate)}</TableCell>
                    <TableCell align="right">{formatCurrency(mailing.avgCpaPerMailing)}</TableCell>
                    <TableCell align="right">{formatCurrency(mailing.cpaEtkp)}</TableCell>
                    <TableCell align="right">
                      <Typography fontWeight="medium" color="primary.main">
                        {formatCurrency(mailing.totalCpaRevenue)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Klick auf Mailing-Zeile filtert die Kampagnen-Tabelle oben
          </Typography>
        </Box>
      )}

      {/* Detail Dialog */}
      <Dialog open={!!selectedCampaign} onClose={handleCloseDetail} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h6">{selectedCampaign?.name}</Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedCampaign?.webinarTitle}
                {selectedCampaign?.webinarDate && ` • ${formatDate(selectedCampaign.webinarDate)}`}
              </Typography>
            </Box>
            <IconButton onClick={handleCloseDetail}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {detailLoading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : detailData ? (
            <Box>
              {groupedSchedules.map((group) => (
                <Box key={group.mailingNumber} sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Mailing #{group.mailingNumber}: {group.subject}
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell
                            sx={{
                              backgroundColor: '#f5f5f5',
                              fontWeight: 'bold',
                            }}
                          >
                            Client
                          </TableCell>
                          <TableCell
                            sx={{
                              backgroundColor: '#f5f5f5',
                              fontWeight: 'bold',
                            }}
                          >
                            ESP
                          </TableCell>
                          <TableCell
                            sx={{
                              backgroundColor: '#f5f5f5',
                              fontWeight: 'bold',
                            }}
                          >
                            Status
                          </TableCell>
                          <TableCell
                            sx={{
                              backgroundColor: '#f5f5f5',
                              fontWeight: 'bold',
                            }}
                          >
                            Gesendet
                          </TableCell>
                          <TableCell
                            sx={{
                              backgroundColor: '#f5f5f5',
                              fontWeight: 'bold',
                            }}
                            align="right"
                          >
                            Empfänger
                          </TableCell>
                          <TableCell
                            sx={{
                              backgroundColor: '#f5f5f5',
                              fontWeight: 'bold',
                            }}
                            align="right"
                          >
                            Öffnungen
                          </TableCell>
                          <TableCell
                            sx={{
                              backgroundColor: '#f5f5f5',
                              fontWeight: 'bold',
                            }}
                            align="right"
                          >
                            Öffnungs-%
                          </TableCell>
                          <TableCell
                            sx={{
                              backgroundColor: '#f5f5f5',
                              fontWeight: 'bold',
                            }}
                            align="right"
                          >
                            Klicks
                          </TableCell>
                          <TableCell
                            sx={{
                              backgroundColor: '#f5f5f5',
                              fontWeight: 'bold',
                            }}
                            align="right"
                          >
                            CTR
                          </TableCell>
                          <TableCell
                            sx={{
                              backgroundColor: '#f5f5f5',
                              fontWeight: 'bold',
                            }}
                            align="right"
                          >
                            Registr.
                          </TableCell>
                          <TableCell
                            sx={{
                              backgroundColor: '#f5f5f5',
                              fontWeight: 'bold',
                            }}
                            align="right"
                          >
                            ANFOs
                          </TableCell>
                          <TableCell
                            sx={{
                              backgroundColor: '#f5f5f5',
                              fontWeight: 'bold',
                            }}
                            align="right"
                          >
                            Umsatz (real)
                          </TableCell>
                          <TableCell
                            sx={{
                              backgroundColor: '#f5f5f5',
                              fontWeight: 'bold',
                            }}
                            align="right"
                          >
                            CPA €
                          </TableCell>
                          <TableCell
                            sx={{
                              backgroundColor: '#f5f5f5',
                              fontWeight: 'bold',
                            }}
                            align="right"
                          >
                            ANFO %
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {group.schedules.map((schedule) => (
                          <TableRow
                            key={schedule.scheduleId}
                            sx={schedule.isNonOpener ? { backgroundColor: '#f0f7ff' } : {}}
                          >
                            <TableCell>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                }}
                              >
                                <Typography fontWeight="medium">{schedule.clientCode}</Typography>
                                {schedule.isNonOpener && (
                                  <Chip
                                    size="small"
                                    label="NO"
                                    variant="outlined"
                                    color="info"
                                    sx={{ fontSize: '0.6rem', height: 18 }}
                                  />
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption" color="text.secondary">
                                {schedule.espType}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="caption"
                                sx={{
                                  px: 1,
                                  py: 0.25,
                                  borderRadius: 1,
                                  backgroundColor:
                                    schedule.status === 'sent' ? '#e8f5e9' : '#fff3e0',
                                  color: schedule.status === 'sent' ? '#2e7d32' : '#e65100',
                                }}
                              >
                                {schedule.status}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {/* sent_at ist Berlin-Wallclock — plain new Date() zeigte +2h */}
                              {schedule.sentAt
                                ? formatGermanTime(schedule.sentAt, 'DD.MM.YYYY, HH:mm:ss')
                                : '-'}
                            </TableCell>
                            <TableCell align="right">
                              {formatNumber(schedule.metrics?.recipients || 0, 0)}
                            </TableCell>
                            <TableCell align="right">
                              {formatNumber(schedule.metrics?.opens || 0, 0)}
                            </TableCell>
                            <TableCell align="right">
                              {formatPercent(schedule.metrics?.openRate || 0)}
                            </TableCell>
                            <TableCell align="right">
                              {formatNumber(schedule.metrics?.clicks || 0, 0)}
                            </TableCell>
                            <TableCell align="right">
                              {formatPercent(schedule.metrics?.ctr || 0)}
                            </TableCell>
                            <TableCell align="right">
                              <Typography fontWeight="medium" color="primary">
                                {formatNumber(schedule.metrics?.registrations || 0, 0)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography fontWeight="medium" color="success.main">
                                {formatNumber(schedule.metrics?.anfos || 0, 0)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2">
                                {formatCurrency(schedule.metrics?.revenue || 0)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" color="primary.main">
                                {formatCurrency(schedule.metrics?.cpaRevenue || 0)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              {formatPercent(schedule.metrics?.anfoRate || 0)}
                            </TableCell>
                          </TableRow>
                        ))}
                        {/* Subtotal row for this mailing */}
                        <TableRow sx={{ backgroundColor: '#fafafa' }}>
                          <TableCell colSpan={4}>
                            <Typography variant="body2" fontWeight="bold">
                              Summe Mailing #{group.mailingNumber}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography fontWeight="bold">
                              {formatNumber(
                                group.schedules.reduce(
                                  (sum, s) => sum + (s.metrics?.recipients || 0),
                                  0,
                                ),
                                0,
                              )}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography fontWeight="bold">
                              {formatNumber(
                                group.schedules.reduce(
                                  (sum, s) => sum + (s.metrics?.opens || 0),
                                  0,
                                ),
                                0,
                              )}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography fontWeight="bold">
                              {formatPercent(
                                group.schedules.reduce(
                                  (sum, s) => sum + (s.metrics?.opens || 0),
                                  0,
                                ) /
                                  Math.max(
                                    group.schedules.reduce(
                                      (sum, s) => sum + (s.metrics?.recipients || 0),
                                      0,
                                    ),
                                    1,
                                  ),
                              )}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography fontWeight="bold">
                              {formatNumber(
                                group.schedules.reduce(
                                  (sum, s) => sum + (s.metrics?.clicks || 0),
                                  0,
                                ),
                                0,
                              )}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography fontWeight="bold">
                              {formatPercent(
                                group.schedules.reduce(
                                  (sum, s) => sum + (s.metrics?.clicks || 0),
                                  0,
                                ) /
                                  Math.max(
                                    group.schedules.reduce(
                                      (sum, s) => sum + (s.metrics?.opens || 0),
                                      0,
                                    ),
                                    1,
                                  ),
                              )}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography fontWeight="bold" color="primary">
                              {formatNumber(
                                group.schedules.reduce(
                                  (sum, s) => sum + (s.metrics?.registrations || 0),
                                  0,
                                ),
                                0,
                              )}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography fontWeight="bold" color="success.main">
                              {formatNumber(
                                group.schedules.reduce(
                                  (sum, s) => sum + (s.metrics?.anfos || 0),
                                  0,
                                ),
                                0,
                              )}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography fontWeight="bold">
                              {formatCurrency(
                                group.schedules.reduce(
                                  (sum, s) => sum + (s.metrics?.revenue || 0),
                                  0,
                                ),
                              )}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography fontWeight="bold" color="primary.main">
                              {formatCurrency(
                                group.schedules.reduce(
                                  (sum, s) => sum + (s.metrics?.cpaRevenue || 0),
                                  0,
                                ),
                              )}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography fontWeight="bold">
                              {formatPercent(
                                group.schedules.reduce(
                                  (sum, s) => sum + (s.metrics?.anfos || 0),
                                  0,
                                ) /
                                  Math.max(
                                    group.schedules.reduce(
                                      (sum, s) => sum + (s.metrics?.registrations || 0),
                                      0,
                                    ),
                                    1,
                                  ),
                              )}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              ))}

              {/* Campaign Total */}
              {detailData.schedules.length > 0 && (
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    backgroundColor: '#e3f2fd',
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Kampagnen-Gesamt
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={2}>
                      <Typography variant="caption" color="text.secondary">
                        Empfänger
                      </Typography>
                      <Typography variant="h6">
                        {formatNumber(
                          detailData.schedules.reduce(
                            (sum, s) => sum + (s.metrics?.recipients || 0),
                            0,
                          ),
                          0,
                        )}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={2}>
                      <Typography variant="caption" color="text.secondary">
                        Öffnungen
                      </Typography>
                      <Typography variant="h6">
                        {formatNumber(
                          detailData.schedules.reduce((sum, s) => sum + (s.metrics?.opens || 0), 0),
                          0,
                        )}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={2}>
                      <Typography variant="caption" color="text.secondary">
                        Ø Öffnungsrate
                      </Typography>
                      <Typography variant="h6">
                        {formatPercent(
                          detailData.schedules.reduce(
                            (sum, s) => sum + (s.metrics?.opens || 0),
                            0,
                          ) /
                            Math.max(
                              detailData.schedules.reduce(
                                (sum, s) => sum + (s.metrics?.recipients || 0),
                                0,
                              ),
                              1,
                            ),
                        )}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={2}>
                      <Typography variant="caption" color="text.secondary">
                        Klicks
                      </Typography>
                      <Typography variant="h6">
                        {formatNumber(
                          detailData.schedules.reduce(
                            (sum, s) => sum + (s.metrics?.clicks || 0),
                            0,
                          ),
                          0,
                        )}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={2}>
                      <Typography variant="caption" color="text.secondary">
                        CTR
                      </Typography>
                      <Typography variant="h6">
                        {formatPercent(
                          detailData.schedules.reduce(
                            (sum, s) => sum + (s.metrics?.clicks || 0),
                            0,
                          ) /
                            Math.max(
                              detailData.schedules.reduce(
                                (sum, s) => sum + (s.metrics?.opens || 0),
                                0,
                              ),
                              1,
                            ),
                        )}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={2}>
                      <Typography variant="caption" color="text.secondary">
                        Registrierungen
                      </Typography>
                      <Typography variant="h6" color="primary">
                        {formatNumber(
                          detailData.schedules.reduce(
                            (sum, s) => sum + (s.metrics?.registrations || 0),
                            0,
                          ),
                          0,
                        )}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={2}>
                      <Typography variant="caption" color="text.secondary">
                        ANFOs
                      </Typography>
                      <Typography variant="h6" color="success.main">
                        {formatNumber(
                          detailData.schedules.reduce((sum, s) => sum + (s.metrics?.anfos || 0), 0),
                          0,
                        )}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={2}>
                      <Typography variant="caption" color="text.secondary">
                        Umsatz (real)
                      </Typography>
                      <Typography variant="h6">
                        {formatCurrency(
                          detailData.schedules.reduce(
                            (sum, s) => sum + (s.metrics?.revenue || 0),
                            0,
                          ),
                        )}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={2}>
                      <Typography variant="caption" color="text.secondary">
                        CPA-Umsatz
                      </Typography>
                      <Typography variant="h6" color="primary.main">
                        {formatCurrency(
                          detailData.schedules.reduce(
                            (sum, s) => sum + (s.metrics?.cpaRevenue || 0),
                            0,
                          ),
                        )}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={2}>
                      <Typography variant="caption" color="text.secondary">
                        Anfos je Registrierung
                      </Typography>
                      <Typography variant="h6">
                        {formatPercent(
                          detailData.schedules.reduce(
                            (sum, s) => sum + (s.metrics?.anfos || 0),
                            0,
                          ) /
                            Math.max(
                              detailData.schedules.reduce(
                                (sum, s) => sum + (s.metrics?.registrations || 0),
                                0,
                              ),
                              1,
                            ),
                        )}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </Box>
          ) : (
            <Typography color="text.secondary">Keine Daten verfügbar</Typography>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default EvaluationTab;
